/**
 * Reglas de los canjes. Módulo PURO: sin red, sin `Date`, sin Supabase.
 *
 * Estas mismas reglas viven en el servidor (migración 0025). Aquí existen para avisarle a la
 * mercadista EN EL MOMENTO, que es cuando le sirve; la garantía la da el servidor. Si esta
 * copia decidiera por su cuenta, podría avisar de un bloqueo que no ocurre —o peor, dejar pasar
 * uno que sí— y el aviso dejaría de valer.
 */

export interface PremioEsperado {
  prizeId: string;
  units: number;
}

export interface DinamicaConfig {
  id: string;
  code: string;
  name: string;
  /** 'monto': compra mínima en soles. 'producto': compra de un producto (paquete de pañal). */
  triggerKind: 'monto' | 'producto';
  /** Umbral en soles. Siempre `null` cuando `triggerKind` es 'producto'. */
  minAmount: number | null;
  esperados: PremioEsperado[];
  /**
   * Cómo se decide el premio (0030).
   *
   * 'esperado' — lo determina la dinámica: la pantalla lo precarga desde `esperados` y solo se
   *   cambia por falta de stock. Es el caso del canje multimarca.
   * 'elegido' — es aleatorio o variable, así que hay que PREGUNTARLO en cada registro y es
   *   obligatorio. Es el caso de un raspa y gana: el premio sale en la raspadita, la dinámica
   *   no lo sabe.
   */
  prizeSelection: 'esperado' | 'elegido';
  /** false = esta dinámica no pide el nombre del cliente (su evidencia son las fotos). */
  requiresClientName: boolean;
  /** Bolsa a la que suma su cantidad para las combinaciones de premio (0407), p. ej. «tira_picante». */
  grupo?: string | null;
  /** Cómo se rotula SU cantidad («tiras», «cajas»). Sin esto, la de la campaña. */
  unidad?: string | null;
}

export interface PremioEntregado {
  prizeId: string;
  units: number;
}

export interface LineaCanje {
  dynamicId: string;
  /** Canjes que el cliente usó de esta dinámica. La SUMA por registro es la que topa. */
  canjes: number;
  /** Unidades entregadas de verdad. Número DISTINTO de `canjes`. */
  premios: PremioEntregado[];
}

/** Cuenta canjes, no líneas: es como cuenta el dashboard del cliente (7.676 canjes). */
export function totalCanjes(lineas: LineaCanje[]): number {
  return lineas.reduce((suma, l) => suma + l.canjes, 0);
}

/**
 * Premio que sale del TOTAL del canje, no de cada línea (0386, Takis Mercados): «cada 3 tiras = 1 Licua-Takis, máximo
 * 3, no importa la variedad». Las líneas dicen cuántas unidades de cada producto se vendieron; el premio se calcula
 * sumándolas todas. `tope` en `null` = sin tope.
 */
export interface ReglaPremioTotal {
  prizeId: string;
  /** Cada cuántas unidades sumadas, 1 premio. Con `combos` no se usa (llega en 0). */
  cada: number;
  tope: number | null;
  /**
   * Combinaciones (0407, Takis Mercados desde el 5-nov): el premio es la MEJOR forma de armarlas con lo vendido, hasta
   * el tope. Cada línea suma a la bolsa de su dinámica (`DinamicaConfig.grupo`). Sin combinaciones, vale `cada`.
   */
  combos?: ComboPremio[] | null;
}

/** «3 tiras picantes + 3 tiras no picantes = 1»: `pide` = {grupo: cantidad}, `da` = premios. */
export interface ComboPremio {
  nombre: string;
  pide: Record<string, number>;
  da: number;
}

/**
 * La mejor forma de armar combinaciones con lo vendido (`totales` = {grupo: cantidad}), hasta `tope`. Recorre
 * combinaciones con repetición en orden (desde `desde`) para no contar dos veces el mismo reparto, y corta al llegar al
 * tope. Es el mismo algoritmo que `premio_por_combos()` de la base (0407), que usa el tablero: los dos con los mismos
 * casos de prueba.
 */
export function premiosPorCombos(
  totales: Record<string, number>, combos: ComboPremio[], tope: number | null, desde = 0,
): number {
  if (tope !== null && tope <= 0) return 0;
  let mejor = 0;
  for (let i = desde; i < combos.length; i++) {
    const c = combos[i];
    if (!(c.da > 0)) continue;
    const resto = { ...totales };
    let alcanza = true;
    for (const [grupo, cantidad] of Object.entries(c.pide)) {
      const hay = Number.isFinite(resto[grupo]) ? resto[grupo] : 0;
      if (hay < cantidad) { alcanza = false; break; }
      resto[grupo] = hay - cantidad;
    }
    if (!alcanza) continue;
    let n = c.da + premiosPorCombos(resto, combos, tope === null ? null : tope - c.da, i);
    if (tope !== null) n = Math.min(n, tope);
    if (n > mejor) mejor = n;
    if (tope !== null && mejor >= tope) break;
  }
  return mejor;
}

/** Cuánto se vendió de cada grupo en el canje. Una línea sin grupo no suma a ninguna combinación. */
export function totalesPorGrupo(
  lineas: LineaCanje[], grupoDe: (dynamicId: string) => string | null | undefined,
): Record<string, number> {
  const t: Record<string, number> = {};
  for (const l of lineas) {
    const g = grupoDe(l.dynamicId);
    if (!g) continue;
    t[g] = (t[g] ?? 0) + (Number.isFinite(l.canjes) ? l.canjes : 0);
  }
  return t;
}

/** Cuántos premios da un total de unidades. 8 tiras con «cada 3» = 2; 12 con tope 3 = 3. */
export function premiosDelTotal(totalUnidades: number, regla: ReglaPremioTotal): number {
  if (!Number.isFinite(totalUnidades) || totalUnidades <= 0) return 0;
  const n = Math.floor(totalUnidades / regla.cada);
  return regla.tope === null ? n : Math.min(n, regla.tope);
}

/**
 * Las líneas con el premio de la regla. El premio va entero en la PRIMERA línea y las demás quedan sin premio: la base
 * guarda premios por línea, y repartirlo entre productos sería inventar de cuál salió cada Licua-Takis. Lo que se cuenta
 * (los tableros suman por canje) da lo mismo.
 */
export function aplicarPremioPorTotal(
  lineas: LineaCanje[], regla: ReglaPremioTotal,
  /** Con combinaciones (0407): a qué grupo suma cada línea. Sin esto, ninguna línea arma combinación. */
  grupoDe: (dynamicId: string) => string | null | undefined = () => null,
): LineaCanje[] {
  const n = regla.combos && regla.combos.length > 0
    ? premiosPorCombos(totalesPorGrupo(lineas, grupoDe), regla.combos, regla.tope)
    : premiosDelTotal(totalCanjes(lineas), regla);
  return lineas.map((l, i) => ({
    ...l,
    premios: i === 0 && n > 0 ? [{ prizeId: regla.prizeId, units: n }] : [],
  }));
}

/** La regla en una frase, para mostrarla junto al premio calculado: quien entrega tiene que poder comprobarla. */
export function describirRegla(regla: ReglaPremioTotal, premio: string, unidadPlural: string): string {
  const tope = regla.tope !== null ? ` Máximo ${regla.tope} por canje.` : '';
  if (regla.combos && regla.combos.length > 0) {
    return regla.combos.map((c) => `${c.nombre} = ${c.da} ${premio}`).join(' · ') + '.' + tope;
  }
  return `1 ${premio} cada ${regla.cada} ${unidadPlural} sumando los productos.${tope}`;
}

/** «6 tiras · 1 caja»: suma cada línea con las de su misma unidad, en el orden en que aparecen. */
export function resumenPorUnidad(
  lineas: LineaCanje[], unidadDe: (dynamicId: string) => string | null | undefined, unidadPorDefecto: string,
): string {
  const suma = new Map<string, number>();
  for (const l of lineas) {
    const u = (unidadDe(l.dynamicId) ?? '').trim() || unidadPorDefecto;
    suma.set(u, (suma.get(u) ?? 0) + (Number.isFinite(l.canjes) ? l.canjes : 0));
  }
  return [...suma].map(([u, n]) => `${n} ${n === 1 && u.endsWith('s') ? u.slice(0, -1) : u}`).join(' · ');
}

/** Cómo se rotula la cantidad de cada línea: «Tiras» si la campaña cuenta tiras, «Canjes» si no. */
export function etiquetaUnidad(unidadPlural: string | null | undefined): string {
  if (unidadPlural == null || unidadPlural.trim() === '') return 'Canjes';
  const u = unidadPlural.trim();
  return u.charAt(0).toUpperCase() + u.slice(1);
}

/** Cuántos canjes le quedan a este cliente. `null` = la campaña no tiene tope. */
export function canjesDisponibles(
  lineas: LineaCanje[], maxPerClient: number | null,
): number | null {
  if (maxPerClient === null) return null;
  return Math.max(0, maxPerClient - totalCanjes(lineas));
}

/** El tope es "máximo N": llegar justo a N no excede. */
export function excedeTope(lineas: LineaCanje[], maxPerClient: number | null): boolean {
  if (maxPerClient === null) return false;
  return totalCanjes(lineas) > maxPerClient;
}

/**
 * Lo que la pantalla precarga: lo esperado por la dinámica, multiplicado por los canjes.
 *
 * Con `prizeSelection = 'elegido'` no precarga NADA, y no por falta de datos: el premio de un
 * raspa y gana lo decide la raspadita, así que cualquier sugerencia sería una invención que la
 * mercadista tendría que corregir en todos los registros.
 */
export function premiosSugeridos(dinamica: DinamicaConfig, canjes: number): PremioEntregado[] {
  if (dinamica.prizeSelection === 'elegido') return [];
  return dinamica.esperados.map((e) => ({ prizeId: e.prizeId, units: e.units * canjes }));
}

/**
 * ¿Lo entregado difiere de lo esperado? (equivalente por falta de stock)
 *
 * Compara por producto y unidades, no por orden: entregar los mismos premios en otro orden es
 * lo esperado, no una equivalencia.
 *
 * Con `prizeSelection = 'elegido'` NUNCA es equivalencia. Sin esta guarda, comparar "lo
 * esperado" (que es vacío a propósito) contra el premio elegido daría `true` en **todos** los
 * registros, y la campaña entera aparecería como "hubo que cambiar el premio por falta de
 * stock" cuando no se cambió ni uno. Una bandera que se prende siempre no informa nada y
 * además tapa los casos en que el stock sí falló.
 */
export function esEquivalencia(linea: LineaCanje, dinamica: DinamicaConfig): boolean {
  if (dinamica.prizeSelection === 'elegido') return false;
  const esperado = premiosSugeridos(dinamica, linea.canjes);
  if (esperado.length !== linea.premios.length) return true;
  const porId = new Map(linea.premios.map((p) => [p.prizeId, p.units]));
  return esperado.some((e) => porId.get(e.prizeId) !== e.units);
}

/**
 * ¿Falta declarar el premio? Devuelve el mensaje para la pantalla, o `null` si está completo.
 *
 * Solo aplica a las dinámicas de premio 'elegido'. En las de premio 'esperado' el premio ya
 * viene precargado y no hay nada que preguntar.
 *
 * Esta regla vive SOLO en el cliente, y es una decisión, no un olvido: `register_canje` acepta
 * un registro con `premios: []`, y ampliarlo para rechazarlo convertiría un descuido de captura
 * en un canje perdido — la cola da el registro por muerto tras 5 rechazos permanentes y el
 * premio ya salió de la caja. Se prefiere exigirlo donde la persona puede corregirlo (la
 * pantalla, con el producto en la mano) y que el panel delate el registro sin premio.
 */
export function faltaPremio(
  lineas: LineaCanje[], dinamicas: DinamicaConfig[],
): string | null {
  const porId = new Map(dinamicas.map((d) => [d.id, d]));
  for (const l of lineas) {
    const d = porId.get(l.dynamicId);
    if (!d || d.prizeSelection !== 'elegido') continue;
    const unidades = l.premios.reduce((s, p) => s + p.units, 0);
    if (unidades <= 0) return `Falta marcar el premio de "${d.name}".`;
  }
  return null;
}

/** Cuántas veces el umbral se considera un monto sospechosamente alto (caso 1492 sobre 100). */
const FACTOR_MONTO_ABSURDO = 10;

/**
 * Aviso sobre el monto, o `null` si no hay nada que decir. NUNCA bloquea: un monto raro es mala
 * calidad de dato, no producto no autorizado. La pantalla lo muestra como confirmación
 * ("Registrar igual / Volver"), igual que la salida antes de tiempo.
 *
 * Solo miran el monto las dinámicas de gatillo 'monto'. Una de producto ("Babysec pack") no
 * exige monto: pedírselo sería una alerta falsa en cada registro, y una alerta falsa repetida
 * enseña a ignorar las alertas.
 */
export function avisoDeMonto(
  monto: number | null, lineas: LineaCanje[], dinamicas: DinamicaConfig[],
): string | null {
  const porId = new Map(dinamicas.map((d) => [d.id, d]));
  const deMonto = lineas
    .map((l) => porId.get(l.dynamicId))
    .filter((d): d is DinamicaConfig =>
      d !== undefined && d.triggerKind === 'monto' && d.minAmount !== null);

  if (deMonto.length === 0) return null;

  // La más exigente manda: con una línea de S/25 y otra de S/100, el mínimo real es 100.
  const exigente = deMonto.reduce((a, b) => ((a.minAmount ?? 0) >= (b.minAmount ?? 0) ? a : b));
  const umbral = exigente.minAmount as number;

  if (monto === null || monto === 0) {
    return `Falta el monto de compra: "${exigente.name}" pide S/${umbral} o más.`;
  }
  if (monto < umbral) {
    return `El monto S/${monto} es menor al mínimo de S/${umbral} de "${exigente.name}".`;
  }
  if (monto > umbral * FACTOR_MONTO_ABSURDO) {
    return `El monto S/${monto} parece muy alto para "${exigente.name}" (mínimo S/${umbral}).`;
  }
  return null;
}
